import { Input, message } from 'antd';
import { memo, useCallback, useContext, useEffect, useState } from 'react';

import Button from '@/components/common/Button';
import Dragger from '@/components/common/Dragger';
import Loading from '@/components/common/Loading';
import { RightContext } from '@/components/RightProvider/context';
import { useFileParser } from '@/hooks/useFileParser';
import { usePartial } from '@/hooks/usePartial';
import { AIFunction } from '@/utils/const';
import { isCnArea } from '@/utils/envHelper';
import { t } from '@/utils/helper/intl';

import { PaymentModal } from '../..';
import { saveAsset, textComplianceReview } from '../../service';
import styles from '../../style.module.scss';
import { isStorageAvailable } from '../../utils';
import { UploadModal } from '..';

const ScriptMaxLength = 5000;

export const ScriptUploadContent: React.FC<{
  type: 'add' | 'update';
  title: string;
  content: string;
  loading: boolean;
  submitLoading: boolean;
  onChangeTitle: (title: string) => void;
  onChangeContent: (content: string) => void;
  onSave: (type: 'add' | 'update' | null, isJump?: boolean) => Promise<void> | void;
}> = memo(
  ({
    type,
    title,
    content,
    loading,
    submitLoading,
    onChangeContent,
    onChangeTitle,
    onSave,
  }) => {
    return (
      <div className="flex flex-col">
        <div className="pt-[12px] flex flex-col gap-[10px]">
          <div className="bg-common-modal-upload-popover-script-input-bg flex items-center p-[10px] gap-[16px] rounded-[6px]">
            <span className="text-[16px] font-[500] leading-[22px] pl-[12px] flex flex-shrink-0">
              {t('bc.sc.title')} :{' '}
            </span>
            <Input
              value={title}
              className={styles['upload-script-input']}
              style={{
                height: 24,
                resize: 'none',
                fontWeight: 400,
                fontSize: 16,
                fontFamily: "'Inter', sans-serif",
              }}
              maxLength={30}
              onChange={(e) => {
                onChangeTitle(e.target.value);
              }}
            />
            <span className="text-[#A3A3A3] text-[14px] tracking-[0.24px]  font-number">
              {title.length}/30
            </span>
          </div>

          <div className="bg-common-modal-upload-popover-script-input-bg flex flex-col  py-[10px] pl-[22px] pr-[0] pb-[0px] gap-[8px] rounded-[6px]">
            <p className="text-[16px] font-[500] leading-[22px]">
              {t('bc.sccontent')} :{' '}
            </p>
            <div className="bg-common-modal-upload-popover-script-input-bg relative">
              {loading && <Loading />}
              <Input.TextArea
                value={content}
                style={{
                  height: 208,
                  resize: 'none',
                  fontSize: '14px',
                  fontWeight: 400,
                  fontFamily: "'Inter', sans-serif",
                }}
                className={styles['upload-script-input']}
                maxLength={ScriptMaxLength}
                onChange={(e) => {
                  onChangeContent(e.target.value);
                }}
              />
              <p
                className={`limit-count text-right py-[6px] px-[8px] text-[14px]  leading-[20px] font-number ${content.length === ScriptMaxLength ? 'text-[#FB3B34]' : 'text-[#A3A3A3]'}`}
              >
                {content.length}/{ScriptMaxLength}
              </p>
            </div>
          </div>
        </div>
        <div className="w-full pt-[15px]  flex  items-center justify-center gap-[10px]">
          <Button
            onClick={() => {
              onSave(null, true);
            }}
            className={`!px-[54px] ${!content || !title ? '' : '!bg-[#A4B8FD] !text-[#2D2F82]'}`}
            disabled={!content || !title}
          >
            {t('bc.sc.use')}
          </Button>
          <Button
            className="!px-[48px]"
            onClick={() => {
              onSave(type);
            }}
            disabled={!content || !title}
            loading={submitLoading}
          >
            {t('bc.sc.save')}
          </Button>
        </div>
      </div>
    );
  },
);
// 脚本上传面板
const ScriptUploadPanel: React.FC<
  Pick<UploadModal, 'rules' | 'draggerSlot' | 'onClose'>
> = ({ rules, draggerSlot, onClose }) => {
  const { text, loading, error, parseFile } = useFileParser({
    maxTextLength: ScriptMaxLength,
  });
  const [submitLoading, setSubmitLoading] = useState(false);
  const rightContext = useContext(RightContext);

  useEffect(() => {
    setInputData({ content: text });
  }, [text]);

  const [inputData, setInputData] = usePartial({
    title: '',
    content: '',
  });
  const [hasSelectedFile, setHasSelectedFile] = useState(false);

  const onSave = async (type: 'add' | 'update' | null, isJump?: boolean) => {
    if (!inputData.title) {
      message.warning(t('merror.tip.pleaseInputTitle'));
      return;
    }
    if (!inputData.content) {
      message.warning(t('merror.tip.pleaseInputContent'));
      return;
    }
    if (isCnArea) {
      try {
        const result = await textComplianceReview(inputData.content);
        if (result.success && result?.data?.reviewState !== 1) {
          message.error(t('The text content is not compliant'));
          return;
        }
      } catch (error) {
        message.error(t('Text content compliance check failed'));
        console.error('文件合规检查catch', error);
        return;
      }
    }

    const blob = new Blob([inputData.content], { type: 'text/plain' });
    const file = new File([blob], 'script.txt', { type: 'text/plain' });

    // 要上传的所有文件所占用的容量大小
    if (!isStorageAvailable(file.size)) {
      PaymentModal.show({
        feature: AIFunction.Other,
        customTip: t('Creativelibrary-space-insufficient'),
      });
      return;
    }
    const params = {
      type: 8,
      title: inputData.title,
      fileUrl: inputData.content,
      desc: String(file.size),
    };
    try {
      setSubmitLoading(true);
      const res = await saveAsset(params);
      if (res.success) {
        if (isJump) {
          localStorage.setItem(
            'avatarScriptInfo',
            JSON.stringify({
              title: inputData.title,
              fileUrl: inputData.content,
            }),
          );
          // 跳转到数字人视频
          localStorage.removeItem('projectUUID');
          window.location.href = window.location.origin + '/lip-sync';
        } else {
          message.success(t('merror.tip.uploadSuccess'));
          rightContext.withUpdate();
          onClose();
        }
      } else {
        message.error(t('merror.tip.uploadFailed'));
      }
    } catch (error) {
      message.error(t('merror.tip.uploadFailed'));
    } finally {
      setSubmitLoading(false);
    }
  };
  const onChangeContent = useCallback((newContent: string) => {
    setInputData({ content: newContent });
  }, []);
  const onChangeTitle = useCallback((newTitle: string) => {
    setInputData({ title: newTitle });
  }, []);

  const handleTextFileUpload = useCallback(
    async (files: FileList) => {
      if (files[0]) {
        setInputData({ title: files[0].name.replace(/\.[^/.]+$/, '') });
        setHasSelectedFile(true);
        await parseFile(files[0]);
      }
    },
    [parseFile],
  );

  return (
    <div className="flex flex-col gap-[12px]">
      {!hasSelectedFile ? (
        <div className=" pb-[24px]">
          <Dragger rules={rules} onFileUpload={handleTextFileUpload} multiple={false}>
            {typeof draggerSlot === 'function' ? draggerSlot() : draggerSlot}
          </Dragger>
        </div>
      ) : (
        <ScriptUploadContent
          type="add"
          title={inputData.title}
          content={inputData.content}
          loading={loading}
          submitLoading={submitLoading}
          onChangeContent={onChangeContent}
          onChangeTitle={onChangeTitle}
          onSave={onSave}
        />
      )}
    </div>
  );
};
export default ScriptUploadPanel;
